import { useEffect, useState } from "react";
import { TextInput, Tooltip } from "@mantine/core";
import { useIntl } from "react-intl";
import classNames from "classnames";
import PhoneInput, { CountryData } from "react-phone-input-2";
import "react-phone-input-2/lib/plain.css";
import InputMask from "react-input-mask";
import { ExclamationIcon } from "@/public/icons/ExclamationIcon";
import { InputLabel, ProfileForm } from "@/interfaces/profile";
import { PhoneType } from "@/interfaces/userDetails";
import { PhoneInput as PhoneInputType } from "@/interfaces/profile";
import {
  SelectInput,
  setInitialValue,
} from "@/components/pages/account/myAccount/profile/profileSelect/ProfileSelect";
import { InputType } from "../Profile";
import styles from "./ProfileInput.module.scss";

type ProfileInputProps = {
  label: InputLabel;
  isEditable?: boolean;
  type: InputType;
  form: ProfileForm;
};

export const ProfileInput = ({
  label,
  isEditable,
  type,
  form,
}: ProfileInputProps) => {
  const intl = useIntl();

  const [phoneInitialValue, setPhoneInitialValue] = useState<string>("");

  let phoneFormValue: PhoneInputType | undefined;
  if (type === InputType.PHONE) {
    phoneFormValue = form.getInputProps(label).value as PhoneInputType;
  }

  useEffect(() => {
    if (phoneFormValue) {
      setPhoneInitialValue(phoneFormValue.phoneNumber);
    }
  }, [phoneFormValue, setPhoneInitialValue]);

  const textInputStyles = () => ({
    root: styles.inputRoot,
    label: styles.inputLabel,
    wrapper: styles.inputWrapper,
    input: classNames(styles.inputField, {
      [styles.inputError]: form.errors[label],
    }),
    disabled: styles.inputDisabled,
    error: styles.inputErrorMessage,
  });

  const phoneInputStyle = () => ({
    containerClass: styles.phoneContainer,
    inputClass: classNames(styles.inputField, styles.phoneInput, {
      [styles.inputDisabled]: !isEditable,
      [styles.inputError]: form.errors[label],
    }),
    buttonClass: classNames(styles.phoneButton, {
      [styles.inputDisabled]: !isEditable,
    }),
    dropdownClass: styles.phoneDropdown,
    searchClass: styles.phoneSearch,
  });

  const formatPhoneValue = (phoneNumber: string, country: CountryData) => {
    const phoneType =
      label === "mobile" ? PhoneType.MOBILE_PHONE : PhoneType.FIXED_LINE;
    const countryCode = Number(country.dialCode);
    const countryCodeName = country.countryCode;
    const isValid =
      country.format && phoneNumber
        ? (country.format.match(/\./g) ?? []).length === phoneNumber.length
        : true;

    form.setFieldValue(label, {
      id: phoneFormValue?.id,
      phoneType: phoneType,
      phoneNumber: phoneNumber,
      countryCode: countryCode,
      countryCodeName: countryCodeName,
      isValid: isValid,
    });
  };

  const setSelectedValue = (value) => {
    form.setFieldValue(label, value);
  };

  const initialSelectIndex: number =
    type === InputType.SELECT
      ? setInitialValue(form.getInputProps(label).value, ["Madame", "Monsieur"])
      : 0;

  return (
    <div className={styles.inputGroup}>
      {type === InputType.TEXT && (
        <TextInput
          classNames={textInputStyles()}
          disabled={!isEditable}
          label={intl.formatMessage({ id: label })}
          {...form.getInputProps(label)}
        />
      )}
      {type === InputType.DATE && (
        <InputMask
          mask="99/99/9999"
          {...form.getInputProps(label)}
          disabled={!isEditable}
          alwaysShowMask={false}
          maskChar={" "}
        >
          {(inputProps) => (
            <TextInput
              classNames={textInputStyles()}
              label={intl.formatMessage({ id: label })}
              disabled={!isEditable}
              {...inputProps}
            />
          )}
        </InputMask>
      )}
      {type === InputType.PHONE && (
        <div className={styles.phoneInputGroup}>
          <div
            className={classNames(styles.inputLabel, styles.phoneInputLabel)}
          >
            {intl.formatMessage({ id: label })}
          </div>
          <PhoneInput
            value={phoneInitialValue}
            preferredCountries={["fr"]}
            disabled={!isEditable}
            {...phoneInputStyle()}
            placeholder={""}
            onChange={formatPhoneValue}
          />
        </div>
      )}
      {type === InputType.SELECT && (
        <SelectInput<string>
          initialIndex={initialSelectIndex}
          label={intl.formatMessage({ id: label })}
          setSelectedValue={setSelectedValue}
          isDisabled={!isEditable}
          selectOptions={["Madame", "Monsieur"]}
          selectOptionsLabels={["Madame", "Monsieur"]}
        />
      )}
      {label in form.errors && (
        <div className={styles.errorIcon}>
          <Tooltip label={form.errors[label]}>
            <ExclamationIcon color={"#c51861"} />
          </Tooltip>
        </div>
      )}
    </div>
  );
};
